import React from 'react';
import PropTypes from 'prop-types';

import {Autocomplete, TextField, CircularProgress} from '@mui/material';

import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import backend from 'utils/backend';

const AdminAutocomplete = (props) => {
  const {
    autocompleteSx,
    label = '',
    selectedAdmin,
    selectedDisplayValue, // Lets the parent override the displayed input value, for example to show email instead of name.
    setSelectedAdmin,
    showEmail = false,
    variant = 'outlined',
    textFieldSx,
  } = props;

  const [internalInputValue, setInternalInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fetchNewData, setFetchNewData] = React.useState(true);

  const inputValue = internalInputValue;

  React.useEffect(() => {
    if (selectedAdmin) {
      setInternalInputValue(selectedDisplayValue ?? `${selectedAdmin.firstName} ${selectedAdmin.lastName}`);
      return;
    }

    setInternalInputValue('');
  }, [selectedAdmin, selectedDisplayValue]);

  React.useEffect(() => {
    if (loading) {
      return;
    } else if (fetchNewData ) {
      const fetchData = async () => {
        const inputToFetchBy = (inputValue || '').toLowerCase();
        const localMatches = options.filter((option) => {
          const optionName = `${option.firstName} ${option.lastName}`.toLowerCase();
          const optionEmail = (option.email || '').toLowerCase();

          return optionName.includes(inputToFetchBy) || optionEmail.includes(inputToFetchBy);
        });

        // If less than 3 local matches, fetch new data
        if (localMatches.length < 3) {
          setLoading(true);

          try {
            const filter = [];
            // if (inputValue) {
            //   filter.push({key: 'firstName', value: `${inputValue.trim()}*`});
            // }
            filter.push({key: 'status', value: ['ACTIVE']});
            filter.push({key: 'role', value: 'ADMIN'});
            const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};

            const result = await backend.user.queryUser(filter, sortBy);

            const list = result.list;
            setOptions(list);
          } catch (e) {
            console.error('Failed to query admin data');
          } finally {
            setLoading(false);
          }
        }
      };
      fetchData();
    }
  }, [inputValue]);

  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'input') {
      setFetchNewData(true);
      setInternalInputValue(newValue); // Always update the inputValue for display
      return;
    }

    if (reason === 'clear') {
      setFetchNewData(false);
      setInternalInputValue('');
      return;
    }

    setFetchNewData(false);

    if (selectedAdmin) {
      setInternalInputValue(selectedDisplayValue ?? `${selectedAdmin.firstName} ${selectedAdmin.lastName}`);
      return;
    }

    setInternalInputValue(newValue);
  };

  const handleOptionSelect = (event, selectedOption) => {
    if (!selectedOption) {
      setSelectedAdmin(null);
      setInternalInputValue('');
      return;
    }

    setSelectedAdmin(selectedOption); // Pass only the id of the selected option to the parent
    if (!selectedDisplayValue) {
      setInternalInputValue(`${selectedOption.firstName} ${selectedOption.lastName}`);
    }
  };

  return (
    <Autocomplete
      options={options}
      sx={autocompleteSx}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.userRn === value.userRn}
      value={selectedAdmin ?? null}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) => option.firstName + ' ' + option.lastName || ''}
      loading={loading}
      filterOptions={(availableOptions, state) => {
        const inputTofilterBy = state.inputValue.toLowerCase();

        return availableOptions.filter((option) => {
          const optionName = `${option.firstName} ${option.lastName}`.toLowerCase();
          const optionEmail = (option.email || '').toLowerCase();

          return !inputTofilterBy || optionName.includes(inputTofilterBy) || optionEmail.includes(inputTofilterBy);
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          sx={{
            'minWidth': 'fit-content',
            '& .MuiInputBase-root': {
              height: 'inherit',
            },
            ...textFieldSx,
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const optionName = `${option.firstName} ${option.lastName}`;
        const matches = match(optionName, inputValue ?? '');
        const parts = parse(optionName, matches);

        return (
          <li {...props} key={option.userRn}>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{fontWeight: part.highlight ? 700 : 400}}
                  >
                    {part.text.replace(/ /g, '\u00A0')}
                  </span>
                ))}
              </span>
              {
                showEmail &&
                option.email &&
                <span style={{fontSize: 12, color: '#6F6A62'}}>
                  {option.email}
                </span>
              }
            </div>
          </li>
        );
      }}
    />

  );
};

AdminAutocomplete.propTypes = {
  autocompleteSx: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  selectedAdmin: PropTypes.object,
  selectedDisplayValue: PropTypes.string,
  setSelectedAdmin: PropTypes.func.isRequired,
  showEmail: PropTypes.bool,
  textFieldSx: PropTypes.object,
  variant: PropTypes.string,
};

export default AdminAutocomplete;
